import type { Response, StoreThis, ID, Maybe } from '@/types'
import type {
  Cart,
  CartDiscounts,
  CartItem,
  CartSum,
  CustomerProduct,
  CustomerProductListItem,
  PaymentSplit,
} from '@/types/customer/products'
import { isNumber } from '@/types/utilities'

import { computed, ref, unref } from 'vue'

import { defineStore } from 'pinia'
import { useUiStore } from '@/stores/ui'

import { findByPropValue, findIndexByPropValue, handleErrors } from '@/helpers'
import { goal } from '@/plugins/metrika'

const resource = 'baskets'
const storeId = 'cart'

export const useCartStore = defineStore(storeId, () => {
  const cartItems = ref<CartItem[]>([])

  const cartItemsAmount = computed(() =>
    cartItems.value.reduce((acc, item) => acc + item.amount, 0)
  )

  const coupon = ref<Cart['coupon']>(null)
  // const giftcard = ref<Cart['giftcard']>(null)
  const giftcard =
    ref<Maybe<{ code: string; confirmation?: string }[] | null>>(null)

  const discounts = ref<Maybe<CartDiscounts>>(null)
  const sum = ref<Maybe<CartSum>>(null)
  const split = ref<Maybe<PaymentSplit>>(null)
  // TODO: потом убрать лишнее
  function handleProduct(product_id?: ID, amount?: number) {
    if (!Number.isInteger(product_id)) return

    isNumber(product_id)

    const index = findIndexByPropValue(
      cartItems.value,
      product_id,
      'product_id'
    )
    if (index === -1) {
      cartItems.value.push({ product_id, amount: amount ?? 1 })
      goal('cart_add')
      return
    }

    if (amount === -1) cartItems.value[index].amount--
    else {
      cartItems.value[index].amount = amount ?? ++cartItems.value[index].amount
    }

    if (cartItems.value[index].amount === 0) {
      cartItems.value.splice(index, 1)
    }
  }

  function canAdd(sizeId?: Maybe<ID>, stock?: number) {
    const cartStock = getProductById(sizeId)?.stock ?? stock ?? null

    return cartStock !== 0
  }

  function resetCart() {
    cartItems.value = []
    coupon.value = null
    giftcard.value = null
    discounts.value = null
    sum.value = null
    split.value = null
  }

  function normalizeGiftcard(
    gc?: { code: string; confirmation?: string } | null
  ): { code: string; confirmation?: string }[] | null {
    if (!gc) return null
    if ('code' in gc && !gc.code && !gc.confirmation) return null
    // если уже массив, фильтруем пустые
    if (Array.isArray(gc)) {
      const filtered = gc.filter((item) => item.code || item.confirmation)
      return filtered.length ? filtered : null
    }
    // одиночный объект
    return [gc]
  }

  const products = ref<Cart['items']>([])
  const cartIsLoading = ref(false)

  async function sendCart(this: StoreThis) {
    const cart: {
      items: CartItem[]
      coupon: Maybe<string>
      giftcard: Maybe<{ code: string; confirmation?: string }[] | null>
      timestamp: Maybe<number>
    } = {
      items: unref(cartItems),
      coupon: unref(coupon),
      giftcard: normalizeGiftcard(unref(giftcard)),
      timestamp: useUiStore()?.customerTimestamp ?? null,
    }
    let error = false
    let validationErrors: Partial<
      Record<
        keyof {
          items: CartItem[]
          coupon: Maybe<string>
          giftcard: Maybe<object | null>
        },
        string[]
      >
    > = {}

    cartIsLoading.value = true

    try {
      const {
        data: { success, errors, data },
      }: Response<Cart> = await this.rest.put(`/c/${resource}`, cart)

      if (!success && errors) {
        if (Object.hasOwn(errors, 'validation')) {
          validationErrors = errors.validation ?? {}
        }
        throw Error(handleErrors(errors))
      }

      const newCartItems: CartItem[] = []

      data.items.forEach((item) => {
        if (!item.stock) return

        newCartItems.push({ product_id: item.id, amount: item.amount })
      })

      cartItems.value = newCartItems

      products.value = data.items ?? []
      coupon.value = data.coupon ?? null
      // giftcard.value = data.giftcard ?? null
      discounts.value = data.discounts ?? null
      sum.value = data.sum ?? null
      split.value = data.split ?? null
    } catch (e) {
      error = true
      console.error(`[${storeId}/sendCart]`, resource, e)
    } finally {
      cartIsLoading.value = false
    }

    return { error, validationErrors }
  }

  function getProductById(id?: Maybe<ID>): Maybe<CustomerProductListItem> {
    return Number.isInteger(id) ? findByPropValue(products.value, id) : null
  }

  function resetProducts() {
    products.value = []
  }

  async function checkGiftcard(
    this: StoreThis,
    code: string,
    confirmation?: string
  ) {
    try {
      const body = { code }
      const query: Record<string, string> = { code }

      const {
        data: { success, errors, data },
      } = await this.rest.get('/c/giftcards/check', { params: query })


      if (!success && errors) {
        throw Error(handleErrors(errors))
      }

      if (!giftcard.value) {
        giftcard.value = []
      }

      return { success: true, data }
    } catch (e) {
      console.error(`[${storeId}/checkGiftcard]`, e)
      return { success: false, error: e }
    }
  }

  return {
    cartItems,
    cartItemsAmount,

    coupon,
    giftcard,
    discounts,
    sum,
    split,

    handleProduct,
    canAdd,
    resetCart,

    products,
    cartIsLoading,
    sendCart,
    getProductById,
    resetProducts,
    checkGiftcard,
  }
})
